import { html } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import { DimensionDefinition  } from "@reporting/rp-types/definition";
import { msg } from "@omegagrid/localize";
import { createRef, ref } from "lit/directives/ref.js";
import { TreeEvent, TreeMenu } from "@omegagrid/tree";
import { Button, dom, constants as coreConstants, ListSortEvent, SortableList, Menu, Location, MenuItem, IconSpec, Dropdown } from "@omegagrid/core";
import { BaseSection } from "./sectionBase";
import { map } from "lit/directives/map.js";
import { DimensionType, getDimensionType, isStandardDimensionAttribute } from "../../common";
import { getDefaultSections } from "../../adapters";
import { ContextAxis, ContextData } from "@reporting/rp-types/analytics";
import { style, itemStyle } from "./sectionAxis.style";
import constants from "../../constants";
import { AxisSettingsSection } from "./sectionAxisSettings";

@customElement('rp-fsection-axes')
export class AxisSection extends BaseSection {

	static styles = [...BaseSection.styles, style];

	@property({type: String})
	buttonLabel = 'Add {axis}-axis dimension';

	@property({type: Array})
	axes: ('X' | 'Y' | 'Z')[] = ['X', 'Y'];

	@property({type: Array})
	periods: DimensionDefinition[] = [];

	@query('og-button.add')
	btnAdd: Button;

	@queryAll('og-sortablelist')
	lists: NodeListOf<SortableList>;

	@query('rp-fsection-axis-settings', true)
	settings: AxisSettingsSection;

	private treeMenuRef = createRef<TreeMenu>();
	get treeMenu() { return this.treeMenuRef.value }

	private itemMenuRef = createRef<Menu>();
	get itemMenu() { return this.itemMenuRef.value }

	private _currentAxisIndex = 0;
	private _currentItemIndex = 0;

	getAxisItems(index: number) {
		if (!this.context) return [];
		const key = index == 0 ? 'x_axis' : 'y_axis';
		let axis = this.context[key];
		if (!axis) {
			axis = [];
			this.context[key] = axis;
		}
		return axis;
	}

	get menuItems() {
		return [...(this.module.module == 'F' ? [{
			k: 'R',
			v: msg('Rows'),
			icon: 'table-cells-rows',
			level: 0,
			collapsed: true
		}, {
			k: 'AB',
			v: msg('Scenarios'),
			icon: 'scroll',
			level: 0,
			collapsed: true
		}] : []), ...(this.module.module == 'A' ? [{
			k: 'IR',
			v: msg('Invoices'),
			icon: 'file-invoice',
			level: 0,
			collapsed: true
		}] : []), ...this.periods.map(period => ({
			k: period.dim_num,
			v: period.description,
			icon: getDefaultSections().get('period').icon,
			level: 0,
			collapsed: true
		})), {
			k: 'I',
			v: msg('Indicators'),
			icon: getDefaultSections().get('indicators').icon,
			level: 0,
			collapsed: true
		}, ...this.dimensions.flatMap(d => ([{
			k: d.dim_num,
			v: d.description,
			icon: getDefaultSections().get('dimensions').icon,
			level: 0,
			collapsed: true
		}, ...(d.properties || []).map(a => ({
			k: `${d.dim_num}:${a.key}`,
			v: a.name,
			level: 1
		}))]))];
	}

	updated() {
		const lists = Array.from(this.lists);
		lists.forEach((list) => list.connectedLists = lists.filter(l => l !== list));
		this.treeMenu?.setSourceData(this.menuItems, {selectMode: 'single', checkboxes: false});
		this.layout();
	}

	openMenu = async (axisIndex: number) => {
		this._currentAxisIndex = axisIndex;
		dom.setSize(this.treeMenu, {w: this.btnAdd.offsetWidth});
		await this.treeMenu.open(this.btnAdd, 'right');
	}

	async openItemMenu(axisIndex: number, index: number, dropdown: Dropdown) {
		dom.showElement(this.settings);
		this.settings.axis = this.getAxisItems(axisIndex)[index];
		this.settings.axisIndex = axisIndex;
		dropdown.dropdown.appendChild(this.settings);
		await this.settings.updateComplete;
		dropdown.layout();
	}

	getDimensionDescription = (axisItem: ContextAxis) : [IconSpec|string, string] => {
		const parts = axisItem.dim.toString().split(':'); // + (axisItem.type || '');
		const dimKey = parts[0];
		const attrKey = parts[1];

		switch (getDimensionType(dimKey as DimensionType)) {
			case 'IR': return [
				'file-invoice',
				msg('Invoices')
			];
			case 'R': return [
				'table-cells-rows',
				msg('Rows')
			];
			case 'AB': return [
				'scroll',
				msg('Scenarios')
			];
			case 'P': return [
				getDefaultSections().get('period').icon,
				this.periods.find(d => d.dim_num == axisItem.dim)?.description || msg('Period')
			];
			case 'I': return [
				getDefaultSections().get('indicators').icon,
				msg('Indicators')
			];
			case 'U': return [
				getDefaultSections().get('dimensions').icon,
				msg('Units')
			];
			case 'D':
				const dimDescription = this.dimensionMap.get(dimKey)?.description || `<${msg('Unknown Dimension')}>`
				const attrDescription = attrKey
					? this.dimensionMap.get(dimKey).properties?.find(p => p.key == attrKey)?.name ?? `<${msg('Unknown Attribute')}>`
					: null;

				return [
					attrDescription ? 'list-dropdown' : getDefaultSections().get('dimensions').icon,
					attrDescription ? `${attrDescription} (${dimDescription})` : dimDescription
				];
			default: return [
				'question',
				'Unknown'
			]
		}
	}

	getItemRenderer = (axisIndex: number) => (div: HTMLDivElement & {_init: boolean}, index: number) => {
		const axisItems = this.getAxisItems(axisIndex);
		const item = axisItems[index];
		const indent = axisItems.reduce((acc, item, i) => acc + (item.column_break && i > 0 && i <= index ? 1 : 0), 0);

		if (!item) {
			div.classList.add('empty-drop');
			return;
		} else {
			div.classList.remove('empty-drop');
		}

		const [icon, description] = this.getDimensionDescription(item);

		const name = item?.name
			? (msg(item?.name) + (description ? ` (${msg(description)}${item.level > 0 ? ` (${item.level})` : ''})` : ''))
			: ((description ? msg(description) : msg('Unknown indicator')) + (item.level > 0 ? ` (${item.level})` : ''));

		return html`
			<div class="item-inner">
				<div style="flex: 0 20px; margin-right: ${15 * indent + 4}px">#${index + 1}</div>

				<div style="${styleMap({
					flex: '1',
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					color: item?.subtotal === false ? '#c58000' : null
				})}">
					<og-icon icon="${icon}" style="width: 12px"></og-icon>
					${name}
				</div>

				<og-dropdown
					noCaret
					icon="gear"
					color="tranparent"
					@dropdown.open="${(e: Event) => this.openItemMenu(axisIndex, index, e.target as Dropdown)}"
					@mousedown="${(e: Event) => e.stopPropagation()}">
				</og-dropdown>

				<og-button
					.icon="${{icon: 'trash', color: 'red'}}"
					color="tranparent"
					@click="${() => this.removeItem(axisIndex, index)}"
					@mousedown="${(e: Event) => e.stopPropagation()}">
				</og-button>
			</div>
		`;
	}

	removeItem = (axisIndex: number, itemIndex: number) => {
		const items = this.getAxisItems(axisIndex);
		if (itemIndex < 0 || itemIndex >= items.length) return;
		const [item] = items.splice(itemIndex, 1);
		this.lists[axisIndex].size = items.length;
		this.layout();
		return item;
	}

	addItem = (axisIndex: number, item: ContextAxis, index?: number) => {
		item.subtotal ??= true;
		const items = this.getAxisItems(axisIndex);
		if (index == null || index >= items.length) {
			items.push(item);
		} else {
			if (index < 0 || index > items.length) return;
			items.splice(index, 0, item);
		}
		this.lists[axisIndex].size = items.length;
		this.layout();
	}

	switch(axisIndex: number) {
		[this.context.x_axis, this.context.y_axis] = [this.context.y_axis, this.context.x_axis];
		this.requestUpdate();
	}

	_onAdd = (e: TreeEvent) => {
		const item: ContextAxis = {dim: e.node.key};
		// a new item inherits the subtotal (and on the y-axis the column break) of the last item
		const last = this.getAxisItems(this._currentAxisIndex).at(-1);
		if (last?.subtotal != null) item.subtotal = last.subtotal;
		if (this._currentAxisIndex === 1 && last?.column_break) item.column_break = true;
		this.addItem(this._currentAxisIndex, item);
	}

	_onSort = (e: ListSortEvent) => {
		console.log(e);
		let { fromIndex, toIndex, targetList } = e;
		const sourceList = e.sourceList || targetList;
		const sourceAxisIndex = parseInt(sourceList.getAttribute('data-index') || '0');
		const targetAxisIndex = parseInt(targetList.getAttribute('data-index') || '0');

		toIndex += e.drop == 'after' && (sourceAxisIndex != targetAxisIndex || toIndex < fromIndex) ? 1 : 0;

		if (sourceAxisIndex !== targetAxisIndex || fromIndex !== toIndex) {
			const item = this.removeItem(sourceAxisIndex, fromIndex);
			this.addItem(targetAxisIndex, item, toIndex);
		}

		this.lists.forEach((list, i) => {
			list.size = this.getAxisItems(i).length;
			list.requestUpdate();
		});

		this.layout();
	}

	_onSortStart = () => {
		this.lists.forEach(list => list.size = Math.max(list.size, 1));
		this.layout();
	}

	updateAxes() {
		//this.updateContext('indicators', this.selectedIndicators);
		this.lists.forEach(list => list.requestUpdate());
		this.layout();
	}

	render = () => html`
		${map(this.axes, (axis, i) => html`
			<div class="axis">
				<div class="axis-header">
					${axis}
					${i < this.axes.length - 1 ? html`
						<og-button
							class="btn-switch-axes"
							icon="arrow-up-arrow-down"
							@click="${() => this.switch(i)}">
						</og-button>
					` : ''}
				</div>

				<div class="axis-body">
					<og-sortablelist
						nativeHeight
						dynamicItemHeight
						disabledSelect
						data-index="${i}"
						@list.sort="${this._onSort}"
						@list.sortstart="${this._onSortStart}"
						style="height: 100%"
						.customStyle="${itemStyle}"
						.size="${this.getAxisItems(i).length}"
						.itemRenderer="${this.getItemRenderer(i)}">
					</og-sortablelist>

					<div class="row row-buttons">
						<og-button
							caret="down"
							class="add"
							color="transparent"
							style="flex: 1"
							@click="${() => this.openMenu(i)}">
							<div slot="content">+ ${msg(this.buttonLabel, {data: { axis }})}</div>
						</og-button>

						<og-button
							type="toggle"
							.state="${this.context && this.context[`${axis.toLowerCase()}_total` as keyof ContextData] ? 'checked' : 'unchecked'}"
							icon="sigma"
							class="total"
							color="accent"
							style="flex: 0 0 20px"
							@click="${(e: PointerEvent) => this.updateContext(`${axis.toLowerCase()}_total`, (e.target as Button).state === 'checked')}"
							@mousedown="${(e: Event) => e.stopPropagation()}">
						</og-button>
					</div>
				</div>
			</div>
		`)}

		<og-tree-menu ${ref(this.treeMenuRef)}
			style="display: none"
			maxHeight="200"
			@select="${this._onAdd}">
		</og-tree-menu>

		<rp-fsection-axis-settings
			@change="${() => this.updateAxes()}"
			slot="inner-content"
			style="display: none">
		</rp-fsection-axis-settings>
	`;

	layout = () => {
		const h = constants.FILTER_PADDING + coreConstants.LIST_ITEM_HEIGHT;
		this.lists?.forEach(list => dom.setSize(list, {h: h * list.size}));
	}

}
